import React, { Component } from 'react';
import { format } from 'date-fns';
import { MARKETING_MODAL, PAYROLL_MODAL } from '../../redux/reducers/modal';
import MarketingStatsModal from '../blocks/marketing/MarketingStatsModal';
import ReduxModal from '../blocks/modal/ReduxModal';
import MainNav from '../blocks/navigation/MainNav';
import PayrollGeneratorModal from '../blocks/payroll/PayrollGeneratorModal';
import {
  generateWeek,
  initialEndDate,
  initialStartDate,
  isSessionLate,
  shiftWeekRange,
} from '../blocks/work_sessions/table/utils';
import WorkSessionsTableMatrix from '../blocks/work_sessions/table/WorkSessionsTableMatrix';
import type { Router, User } from '../../types';
import type {
  DayStatistics,
  LateUserStatistics,
  UserStatistics,
  WorkSessionsByUser,
} from '../blocks/work_sessions/table/types';

type Props = {
  users: Array<User>;
  workSessionsByUser: WorkSessionsByUser;
  statisticsByUser: Array<UserStatistics>;
  statisticsByDay: Array<DayStatistics>;
  lateStatisticsByUser: Array<LateUserStatistics>;
  startDate: string;
  endDate: string;
  isLoading: boolean;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onSubmitFetch: () => void;
  openModal: Function,
  closeModal: Function,
  router: Router,
  currentUser: User,
};

type State = {
  showOnlyLateFilter: boolean;
  displayedStartDate: string;
};

class WorkSessionsTable extends Component<Props, State> {
  state: State = {
    showOnlyLateFilter: false,
    displayedStartDate: this.props.startDate || initialStartDate,
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (!nextProps.isLoading && nextProps.startDate && nextProps.startDate !== prevState.displayedStartDate) {
      return { displayedStartDate: nextProps.startDate };
    }

    return null;
  }

  toggleLateFilter = () => {
    this.setState(prevState => ({
      showOnlyLateFilter: prevState ? !prevState.showOnlyLateFilter : true
    }));
  };

  handlePayrollGeneratorClick = () => {
    const { openModal } = this.props;
    openModal({ id: PAYROLL_MODAL });
  };

  handlePayrollModalClose = () => {
    const { closeModal } = this.props;
    closeModal({ id: PAYROLL_MODAL });
  };

  handleMarketingStatsClick = () => {
    const { openModal } = this.props;
    openModal({ id: MARKETING_MODAL });
  };

  handleMarketingModalClose = () => {
    const { closeModal } = this.props;
    closeModal({ id: MARKETING_MODAL });
  };

  componentDidMount() {
    const { startDate, onStartDateChange, onEndDateChange, onSubmitFetch } = this.props;

    // SELF-HEALING LOGIC:
    // If the parent passes an empty date, initialize it to the current week and fetch!
    if (!startDate) {
      onStartDateChange(initialStartDate);
      onEndDateChange(initialEndDate);

      setTimeout(() => {
        onSubmitFetch();
      }, 0);
    } else {
      onSubmitFetch();
    }
  }

  handleWeekChange = (direction: 'prev' | 'next') => {
    const { onStartDateChange, onEndDateChange, onSubmitFetch } = this.props;
    const { displayedStartDate } = this.state;

    const effectiveStart = displayedStartDate || initialStartDate;
    const nextRange = shiftWeekRange(effectiveStart, direction);

    onStartDateChange(nextRange.startDate);
    onEndDateChange(nextRange.endDate);

    setTimeout(() => {
      onSubmitFetch();
    }, 0);
  };

  render(): React.ReactNode {
    const { users, workSessionsByUser, statisticsByDay, statisticsByUser, lateStatisticsByUser, startDate, isLoading, router, currentUser } = this.props;
    const { showOnlyLateFilter, displayedStartDate } = this.state;

    const displayedUsers = showOnlyLateFilter
      ? users.filter(user => {
        const userSessions = workSessionsByUser[user.id] || [];

        return userSessions.some(isSessionLate);
      })
      : users;

    const effectiveStartDate = displayedStartDate || startDate || initialStartDate;
    const dates = generateWeek(effectiveStartDate);

    return (
      <div className="work-sessions-table container-view view-wrapper">
        {/* Navigation Controls */}
        <MainNav
          currentUser={currentUser}
          navigate={router.navigate}
          onPayrollGeneratorClick={this.handlePayrollGeneratorClick}
          onMarketingStatsClick={this.handleMarketingStatsClick}
        />
        <div className="page-header">
          <div className="header-title">
            <h2>Check-ins</h2>
            <hr className="float-left"></hr>
          </div>
          <div className="filters-header" style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '20px' }}>
            <button
              onClick={() => this.handleWeekChange('prev')}
              disabled={isLoading}
            >
              &laquo; Previous Week
            </button>

            <h3 style={{ margin: 0, minWidth: '200px', textAlign: 'center' }}>
              {dates.length > 0 && `${format(dates[0], 'MMM dd')} - ${format(dates[6], 'MMM dd')}, ${format(dates[0], 'yyyy')}`}
            </h3>

            <button
              onClick={() => this.handleWeekChange('next')}
              disabled={isLoading}
            >
              Next Week &raquo;
            </button>

            <span style={{ marginLeft: '10px', minWidth: '70px', visibility: isLoading ? 'visible' : 'hidden' }}>
              Loading...
            </span>

          </div>
        </div>

        {/* The Matrix Table */}
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <WorkSessionsTableMatrix
            dates={dates}
            displayedUsers={displayedUsers}
            workSessionsByUser={workSessionsByUser}
            statisticsByUser={statisticsByUser}
            statisticsByDay={statisticsByDay}
            lateStatisticsByUser={lateStatisticsByUser}
            showOnlyLateFilter={showOnlyLateFilter}
            onToggleLateFilter={this.toggleLateFilter}
          />
        </div>
        <ReduxModal id={PAYROLL_MODAL}>
          <PayrollGeneratorModal
            onCloseModal={this.handlePayrollModalClose}
          />
        </ReduxModal>
        <ReduxModal id={MARKETING_MODAL}>
          <MarketingStatsModal
            onCloseModal={this.handleMarketingModalClose}
          />
        </ReduxModal>
      </div>
    );
  }
}

export default WorkSessionsTable;
